<template>
    <Head :title="t('assessments.admin.hub.pageTitle')" />
    <BaseLayout>
        <BasePageHeader :title="t('assessments.admin.hub.pageTitle')" :subtitle="t('assessments.admin.hub.pageSubtitle')">
            <template #eyebrow>
                <a href="/reflections" class="body-small text-ink-muted hover:text-ink flex items-center gap-1">
                    <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("assessments.admin.hub.backLink") }}
                </a>
            </template>
        </BasePageHeader>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("assessments.admin.hub.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <section v-else class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
                <h2 class="label-section">{{ t("assessments.admin.hub.campaignsHeading") }}</h2>
                <BaseButton theme="primary" size="sm" :disabled="templates.length === 0" @click="openCreateDialog">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i> {{ t("assessments.admin.hub.newCampaign") }}
                </BaseButton>
            </div>

            <div v-if="campaigns.length === 0" class="rounded-xl border border-dashed border-border bg-surface-raised px-5 py-6 body-small text-ink-muted">
                {{ t("assessments.admin.hub.noCampaigns") }}
            </div>

            <div v-for="campaign in campaigns" :key="campaign.id" class="relative">
                <BaseCard padding="md" as="a" :href="`/reflections/admin/campaigns/${campaign.id}/results`" class="flex items-center justify-between gap-4">
                    <div class="flex-1 min-w-0">
                        <div class="label-section" :class="stateColor(campaign.state)">
                            {{ campaignStateLabel(campaign.state) }}
                        </div>
                        <BaseHeader size="s" class="text-ink truncate mt-1">{{ campaign.name }}</BaseHeader>
                        <div class="body-small text-ink-muted mt-1">{{ campaignMetaLine(campaign) }}</div>
                        <div v-if="campaign.closes_at" class="details text-ink-muted mt-1">
                            {{ t("assessments.admin.hub.closesOn", { date: formatDate(campaign.closes_at) }) }}
                        </div>
                        <div v-if="campaign.has_report" class="details text-ink-muted mt-1">
                            {{ t("assessments.admin.hub.reportGenerated", { date: formatDate(campaign.report_generated_at!) }) }}
                            <span v-if="campaign.new_submissions_since_report > 0" class="text-notification-warning-text font-medium">
                                {{ t("assessments.admin.hub.newSinceReport", { count: campaign.new_submissions_since_report }) }}
                            </span>
                        </div>
                        <div v-else class="details text-ink-muted mt-1">{{ t("assessments.admin.hub.noReportYet") }}</div>
                    </div>
                    <div class="flex items-center gap-3 shrink-0">
                        <StatusChip :state="stateChipState(campaign.state)">{{ campaignStateLabel(campaign.state) }}</StatusChip>
                        <i class="bi bi-arrow-right text-ink-muted" aria-hidden="true"></i>
                    </div>
                </BaseCard>
                <div class="absolute top-3 right-3 z-10 flex items-center gap-2">
                    <BaseButton
                        v-if="campaign.state === 'draft'"
                        type="button"
                        theme="primary"
                        size="sm"
                        :disabled="transitioningId === campaign.id"
                        @click.stop.prevent="launchCampaign(campaign)">
                        <i class="bi bi-send" aria-hidden="true"></i>
                        {{ transitioningId === campaign.id ? t("assessments.admin.hub.launching") : t("assessments.admin.hub.launch") }}
                    </BaseButton>
                    <BaseButton
                        v-if="campaign.state !== 'closed'"
                        type="button"
                        theme="neutral"
                        size="sm"
                        :disabled="transitioningId === campaign.id"
                        @click.stop.prevent="closeCampaign(campaign)">
                        <i class="bi bi-x-circle" aria-hidden="true"></i> {{ t("common.close") }}
                    </BaseButton>
                </div>
            </div>
            <BaseNotification v-if="actionStatus" :theme="actionStatus.theme" :text="actionStatus.text" @dismiss="actionStatus = null" />
        </section>

        <section v-if="teamsEnabled && !loading" class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
                <h2 class="label-section">{{ t("assessments.admin.hub.teamsHeading") }}</h2>
                <BaseButton theme="primary" size="sm" @click="importOpen = true">
                    <i class="bi bi-file-earmark-arrow-up" aria-hidden="true"></i> {{ t("assessments.admin.hub.importTeamCsv") }}
                </BaseButton>
            </div>

            <div v-if="teams.length === 0" class="rounded-xl border border-dashed border-border bg-surface-raised px-5 py-6 body-small text-ink-muted">
                {{ t("assessments.admin.hub.noTeams") }}
            </div>

            <BaseCard v-for="team in teams" :key="team.team_id" padding="md" class="flex items-center justify-between gap-4">
                <div class="min-w-0">
                    <div class="body-small-bold text-ink truncate">{{ team.name || t("assessments.admin.hub.unnamedTeam") }}</div>
                    <div class="body-small text-ink-muted mt-1">{{ teamSummaryLine(team) }}</div>
                </div>
            </BaseCard>
        </section>

        <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
    </BaseLayout>

    <TeamCsvImportDialog v-model:open="importOpen" @imported="reloadTeams" />

    <BaseDialog v-model:open="createOpen" :title="t('assessments.admin.hub.newCampaign')">
        <form id="new-campaign-form" class="flex flex-col gap-4" @submit.prevent="submitCreate">
            <label for="campaign-name" class="block">
                <span class="block body-small-bold text-ink mb-2">{{ t("assessments.admin.hub.nameLabel") }}</span>
                <input
                    id="campaign-name"
                    v-model="form.name"
                    type="text"
                    required
                    maxlength="255"
                    :placeholder="t('assessments.admin.hub.namePlaceholder')"
                    class="w-full px-3 py-2 border border-control-border rounded-lg outline-none focus:border-control-border-focus body-small" />
                <span class="block details text-ink-muted mt-1">{{ t("assessments.admin.hub.nameHint") }}</span>
            </label>

            <fieldset>
                <legend class="block body-small-bold text-ink mb-2">{{ t("assessments.admin.hub.typeLegend") }}</legend>
                <div class="flex flex-col gap-2">
                    <label for="campaign-type-360" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-360" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="false" class="mt-1" />
                        <span>
                            <span class="block body-small-bold text-ink">{{ t("assessments.admin.hub.type360Title") }}</span>
                            <span class="block body-small text-ink-muted mt-1">{{ t("assessments.admin.hub.type360Desc") }}</span>
                        </span>
                    </label>
                    <label for="campaign-type-self" class="flex items-start gap-3 cursor-pointer">
                        <input id="campaign-type-self" v-model="form.isSelfOnly" type="radio" name="campaign-type" :value="true" class="mt-1" />
                        <span>
                            <span class="block body-small-bold text-ink">{{ t("assessments.admin.hub.selfAssessmentOnly") }}</span>
                            <span class="block body-small text-ink-muted mt-1">{{ t("assessments.admin.hub.typeSelfDesc") }}</span>
                        </span>
                    </label>
                </div>
            </fieldset>

            <label for="campaign-template" class="block">
                <span class="block body-small-bold text-ink mb-2">{{ t("assessments.admin.hub.templateLabel") }}</span>
                <select
                    id="campaign-template"
                    v-model="form.templateId"
                    required
                    class="w-full px-3 py-2 border border-control-border rounded-lg outline-none focus:border-control-border-focus body-small">
                    <option v-for="templateOption in eligibleTemplates" :key="templateOption.id" :value="templateOption.id">{{ templateOption.name }}</option>
                </select>
                <span v-if="eligibleTemplates.length === 0" class="block details text-notification-error-text mt-1">
                    {{ t("assessments.admin.hub.noTemplatesForType") }}
                </span>
                <span v-if="selectedTemplate" class="block details text-ink-muted mt-1">
                    {{ t("assessments.admin.hub.questionCount", { count: selectedTemplate.question_count }) }}
                </span>
            </label>

            <label v-if="!form.isSelfOnly" for="campaign-min-responses" class="block">
                <span class="block body-small-bold text-ink mb-2">{{ t("assessments.admin.hub.minResponsesLabel") }}</span>
                <input
                    id="campaign-min-responses"
                    v-model.number="form.minResponses"
                    type="number"
                    min="0"
                    max="32767"
                    required
                    class="w-32 px-3 py-2 border border-control-border rounded-lg outline-none focus:border-control-border-focus body-small" />
                <span class="block details text-ink-muted mt-1">{{ t("assessments.admin.hub.minResponsesHint") }}</span>
            </label>

            <label v-if="!form.isSelfOnly" for="campaign-self-assess" class="flex items-start gap-3 cursor-pointer">
                <input id="campaign-self-assess" v-model="form.selfAssessEnabled" type="checkbox" class="mt-1" />
                <span>
                    <span class="block body-small-bold text-ink">{{ t("assessments.admin.hub.selfAssessIncludeTitle") }}</span>
                    <span class="block body-small text-ink-muted mt-1">{{ t("assessments.admin.hub.selfAssessIncludeDesc") }}</span>
                </span>
            </label>

            <FormField id="campaign-closes-at" :label="t('assessments.admin.hub.closeDateLabel')" :hint="t('assessments.admin.hub.closeDateHint')">
                <template #label
                    >{{ t("assessments.admin.hub.closeDateLabel") }} <span class="text-ink-muted">{{ t("common.optional") }}</span></template
                >
                <BaseInput v-model="form.closesOn" type="date" size="compact" :min="todayIso" />
            </FormField>

            <BaseNotification v-if="createError" theme="error" :text="createError" hide-dismiss />

            <p class="details text-ink-muted text-right">{{ t("assessments.admin.hub.draftsHint") }}</p>
        </form>
        <template #footer>
            <BaseButton theme="neutral" size="sm" :disabled="creating" @click="createOpen = false">{{ t("common.cancel") }}</BaseButton>
            <BaseButton form="new-campaign-form" type="submit" theme="primary" size="sm" :disabled="creating || !canSubmit">{{
                creating ? t("assessments.admin.hub.creating") : t("assessments.admin.hub.createCampaign")
            }}</BaseButton>
        </template>
    </BaseDialog>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { AdminCampaignResponse, AdminCampaignSummary, AdminHubPayload, TemplateSummary } from "~vue/api/generated/reflections";
import type { TeamOverview, TeamsOverviewResponse } from "~vue/api/generated/teams-csv";
import TeamCsvImportDialog from "~vue/AssessmentAdmin/TeamCsvImportDialog.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseHeader from "~vue/components/BaseHeader.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const campaigns = ref<AdminCampaignSummary[]>([]);
const templates = ref<TemplateSummary[]>([]);
const loading = ref(true);
const loadError = ref<string | null>(null);
const transitioningId = ref<string | null>(null);
const actionStatus = ref<{ theme: "success" | "error"; text: string } | null>(null);

const teams = ref<TeamOverview[]>([]);
const teamsEnabled = ref(false);
const importOpen = ref(false);

const createOpen = ref(false);
const creating = ref(false);
const createError = ref<string | null>(null);
const form = ref({
    name: "",
    isSelfOnly: false,
    templateId: null as string | null,
    minResponses: 0,
    selfAssessEnabled: true,
    closesOn: "",
});

const todayIso = new Date().toISOString().slice(0, 10);
// Templates usable for the chosen campaign type: self-only campaigns can't
// use 360-only templates and vice versa (mirrors the backend validation).
const eligibleTemplates = computed<TemplateSummary[]>(() =>
    templates.value.filter((t) => (form.value.isSelfOnly ? t.assessment_mode !== "rater_360" : t.assessment_mode !== "self_only")),
);
const selectedTemplate = computed<TemplateSummary | null>(() => eligibleTemplates.value.find((t) => t.id === form.value.templateId) ?? null);
const canSubmit = computed(() => form.value.name.trim().length > 0 && selectedTemplate.value !== null && form.value.minResponses >= 0);
const campaignDemoActions = computed(() => campaigns.value.filter((campaign) => campaign.state !== "closed").map((campaign) => ({ id: campaign.id, label: campaign.name })));

// Keep the picker valid when the campaign type changes.
watch(eligibleTemplates, (eligible) => {
    if (!eligible.some((t) => t.id === form.value.templateId)) {
        form.value.templateId = eligible[0]?.id ?? null;
    }
});

// Snapshot the template's default minimum whenever the picker changes.
watch(selectedTemplate, (template) => {
    if (template) {
        form.value.minResponses = template.min_responses;
    }
});

function stateColor(state: string): string {
    return state === "active" ? "text-notification-success-text" : "text-ink-muted";
}

function stateChipState(state: string) {
    switch (state) {
        case "active":
            return "active";
        case "draft":
            return "neutral";
        case "closed":
            return "neutral";
        default:
            return "neutral";
    }
}

function campaignStateLabel(state: string): string {
    switch (state) {
        case "active":
            return t("assessments.admin.hub.campaignState.active");
        case "draft":
            return t("assessments.admin.hub.campaignState.draft");
        default:
            return t("assessments.admin.hub.campaignState.closed");
    }
}

function campaignMetaLine(campaign: AdminCampaignSummary): string {
    const counts = { template: campaign.template.name, participants: campaign.cycle_count, responses: campaign.submitted_response_count };
    return campaign.is_self_assessment_only ? t("assessments.admin.hub.metaSelfOnly", counts) : t("assessments.admin.hub.meta", counts);
}

function teamSummaryLine(team: TeamOverview): string {
    const lead = team.lead_names.length ? t("assessments.admin.hub.teamLead", { names: team.lead_names.join(", ") }) : t("assessments.admin.hub.noLeadAssigned");
    return `${lead} · ${t("assessments.admin.hub.memberCount", { count: team.member_count })}`;
}

// The teams surface 404s when team_assessment_visibility is off for the org;
// the section simply stays hidden in that case.
async function reloadTeams(): Promise<void> {
    const { data, error: fetchError } = await useFetch("/v1/teams").json<TeamsOverviewResponse>();
    if (fetchError.value || !data.value) {
        teamsEnabled.value = false;
        return;
    }
    teamsEnabled.value = true;
    teams.value = data.value.teams;
}

onMounted(async () => {
    void reloadTeams();
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns").json<AdminHubPayload>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || t("assessments.admin.hub.loadFailed");
        return;
    }
    campaigns.value = data.value?.campaigns ?? [];
    templates.value = data.value?.templates ?? [];
});

function openCreateDialog(): void {
    createError.value = null;
    const first = templates.value.find((t) => t.assessment_mode !== "self_only") ?? null;
    form.value = {
        name: "",
        isSelfOnly: false,
        templateId: first?.id ?? null,
        minResponses: first?.min_responses ?? 0,
        selfAssessEnabled: true,
        closesOn: "",
    };
    createOpen.value = true;
}

async function submitCreate(): Promise<void> {
    creating.value = true;
    createError.value = null;
    try {
        const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns")
            .post(
                JSON.stringify({
                    name: form.value.name.trim(),
                    template_id: form.value.templateId,
                    is_self_assessment_only: form.value.isSelfOnly,
                    min_responses: form.value.isSelfOnly ? 0 : form.value.minResponses,
                    self_assess_enabled: form.value.isSelfOnly ? true : form.value.selfAssessEnabled,
                    // End of the selected day, in the admin's local timezone.
                    closes_at: form.value.closesOn ? new Date(`${form.value.closesOn}T23:59:59`).toISOString() : null,
                }),
                "application/json",
            )
            .json<AdminCampaignResponse>();
        if (fetchError.value) {
            createError.value = (data.value as { error?: string } | null)?.error || t("assessments.admin.hub.createFailed");
            return;
        }
        if (data.value) {
            campaigns.value = [data.value.campaign, ...campaigns.value];
        }
        createOpen.value = false;
        actionStatus.value = { theme: "success", text: t("assessments.admin.hub.createSuccess", { name: form.value.name.trim() }) };
    } finally {
        creating.value = false;
    }
}

async function transitionCampaign(campaign: AdminCampaignSummary, action: "launch" | "close"): Promise<void> {
    transitioningId.value = campaign.id;
    actionStatus.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}/${action}`, { method: "POST" }).json<AdminCampaignResponse>();
    transitioningId.value = null;
    if (fetchError.value) {
        const fallback = action === "launch" ? t("assessments.admin.hub.couldNotLaunch") : t("assessments.admin.hub.couldNotClose");
        actionStatus.value = { theme: "error", text: (data.value as { error?: string } | null)?.error || fallback };
        return;
    }
    if (data.value) {
        campaigns.value = campaigns.value.map((existing) => (existing.id === campaign.id ? (data.value as AdminCampaignResponse).campaign : existing));
    }
    actionStatus.value = {
        theme: "success",
        text: action === "launch" ? t("assessments.admin.hub.launchSuccess", { name: campaign.name }) : t("assessments.admin.hub.closeSuccess", { name: campaign.name }),
    };
}

async function launchCampaign(campaign: AdminCampaignSummary): Promise<void> {
    if (!globalThis.confirm(t("assessments.admin.hub.confirmLaunch", { name: campaign.name }))) {
        return;
    }
    await transitionCampaign(campaign, "launch");
}

async function closeCampaign(campaign: AdminCampaignSummary): Promise<void> {
    if (!globalThis.confirm(t("assessments.admin.hub.confirmClose", { name: campaign.name }))) {
        return;
    }
    await transitionCampaign(campaign, "close");
}
</script>
